import { Table } from '../components/main/review/human-validation/models/table';
import { PHYSICAL_DOCUMENT_TYPES } from './constants';

const NON_CORE_RANK = 99;

/**
 * Rank used to group statements by type. Core statements (Income Statement,
 * Balance Sheet, Cash Flow) sort ahead of everything else, in the order they
 * appear in PHYSICAL_DOCUMENT_TYPES.
 */
function statementTypeRank(statementType: string): number {
  const index = PHYSICAL_DOCUMENT_TYPES.indexOf(statementType);
  return index === -1 ? NON_CORE_RANK : index;
}

/**
 * Deterministic ordering used when assigning statement labels and their numeric
 * suffixes (e.g. "Income Statement", "Income Statement 2").
 *
 * The suffix must follow the table's position in the document, not the order the
 * API happened to return tables in. The `/tables` endpoint is not page-ordered
 * and its order changes when a user draws a new table, which previously caused
 * two same-type statements (e.g. Page 6 and Page 7 Income Statements) to swap
 * their labels after navigating away and back.
 *
 * Order: by statement-type rank, then by page (sourcePageNumber), then by
 * vertical position and origin so two same-type tables on a single page remain
 * stable regardless of API ordering.
 */
export function compareTablesForLabeling(a: Table, b: Table): number {
  const rankDelta = statementTypeRank(a.statementType) - statementTypeRank(b.statementType);
  if (rankDelta !== 0) return rankDelta;

  const pageDelta = (a.sourcePageNumber ?? 0) - (b.sourcePageNumber ?? 0);
  if (pageDelta !== 0) return pageDelta;

  const topDelta = (a.boundingBox?.y1 ?? 0) - (b.boundingBox?.y1 ?? 0);
  if (topDelta !== 0) return topDelta;

  return (a.origin ?? '').localeCompare(b.origin ?? '');
}
